import { useState } from "react";
import axios from "axios";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { API_BASE, useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { ICH_LOGO } from "@/App";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const initialToken = params.get("token") || "";
  const [token, setToken] = useState(initialToken);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, { token, password }, { withCredentials: true });
      await refresh();
      toast.success("Password updated. You're signed in.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-6 sm:px-10 py-12 flex flex-col" data-testid="reset-password-root">
      <div className="grain" />
      <nav className="max-w-[1400px] w-full mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center overflow-hidden" style={{ border: "1px solid rgba(232,226,212,0.25)" }}>
            <img src={ICH_LOGO} alt="ICH" className="w-full h-full object-contain p-1" />
          </div>
          <div className="font-label text-[#efe7d2]">Innovative Capital Holdings</div>
        </Link>
        <Link to="/login" className="nav-link inline-flex items-center gap-2"><ArrowLeft size={12} /> Sign in</Link>
      </nav>

      <div className="flex-1 flex items-center justify-center py-16">
        <div className="w-full max-w-md glass p-10">
          <div className="font-label text-[rgba(232,226,212,0.55)] mb-4">Reset Password</div>
          <h1 className="font-display text-[#efe7d2] text-4xl sm:text-5xl leading-[1.05]">Set a new <span className="font-italic-display">password.</span></h1>
          <p className="mt-4 text-[rgba(232,226,212,0.65)] text-sm">Paste the token you received from Devontae and choose a new password.</p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="reset-form">
            <div>
              <label htmlFor="rtoken" className="font-label text-[rgba(232,226,212,0.55)] block mb-3">Reset token</label>
              <input id="rtoken" data-testid="reset-token" value={token} onChange={(e) => setToken(e.target.value)} required className="w-full bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-3 text-[#efe7d2] font-display text-base" placeholder="Paste the long token from your reset link" />
            </div>
            <div>
              <label htmlFor="rpass" className="font-label text-[rgba(232,226,212,0.55)] block mb-3">New password</label>
              <input id="rpass" type="password" data-testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="w-full bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-3 text-[#efe7d2] font-display text-xl" placeholder="Min 8 characters" />
            </div>
            <div>
              <label htmlFor="rconf" className="font-label text-[rgba(232,226,212,0.55)] block mb-3">Confirm password</label>
              <input id="rconf" type="password" data-testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="w-full bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-3 text-[#efe7d2] font-display text-xl" placeholder="Re-enter password" />
            </div>
            <button data-testid="reset-submit" disabled={busy} type="submit" className="btn-solid w-full justify-center disabled:opacity-60">
              {busy ? "Updating..." : "Update password"} <ArrowUpRight size={14} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
